import React, { Suspense, useCallback, useEffect, useState } from "react";
import { DialogStaticProps } from "../../../@types/components/dialogProps.types";
import { MulterGridFSFile } from "../../../@types/files.types";
import { generateFileURL } from "../../../utils/commonFunctions/generateFileURL";
import { trackPromise } from "react-promise-tracker";
import { api } from "../../../utils/api";
import generateImageUrl from "../../../utils/commonFunctions/imageUrl";
import { useNavigate } from "react-router-dom";
import useRouteParam from "../../../hooks/useRouteParam";
import { IconButton, Paper, Tooltip, Typography } from "@mui/material";
import { Button, Grid } from "@material-ui/core";
import { dateTimeZoneConverter } from "../../../utils/commonFunctions/date";
import { LazyCustomImageDialogWithTitle, LazyCustomPdfViewWithTitleDialog } from "..";
import DependentEligibilityCard from "./DependentEligibilityCard";
import CoverageForCard from "./CoverageForCard";
import BenefitAmountCard from "./BenefitAmountCard";
import AgeCriteriaCard from "./AgeCriteriaCard";
import StandardPlanDependencyCard from "./StandardPlanDependencyCard";
import InfoRow from "./InfoRow";
import PlanInfoRow from "./PlanInfoRow";
import VisibilityIcon from "@material-ui/icons/Visibility";
import VisibilityOffIcon from "@material-ui/icons/VisibilityOff";
import GetAppRoundedIcon from "@material-ui/icons/GetAppRounded";
import ErrorIcon from "@material-ui/icons/Error";
import { downloadFileFromMongoDB } from "../../../utils/commonFunctions/downloadFileFromDatabase";

const VEBDialogStaticForm = ({ planDetails }: DialogStaticProps) => {
	const [eventName, setEventName] = useState("");
	const [pdfProps, setPdfProps] = useState("");
	const [viewableDocument, setViewableDocument] = useState(false);

	const queryGroupNumber = useRouteParam("gn");
	const navigate = useNavigate();

	const [vebPlanDocument, setVEBPlanDocument] = useState<{
		document: File | null;
	} | null>(null);

	const [imageDialogProps, setImageDialogProps] = useState({
		openDialog: false,
		image: undefined as string | undefined
	});

	const [openPdfViewDialogProps, setOpenPdfViewDialogProps] = useState({
		openDialog: false
	});

	/* ---------------- VIEWABLE DOCUMENT CHECK ---------------- */
	useEffect(() => {
		if (!planDetails?.document) {
			setViewableDocument(false);
			return;
		}

		const { mimetype } = planDetails.document as MulterGridFSFile;
		setViewableDocument(["application/pdf", "image/png", "image/jpg", "image/jpeg"].includes(mimetype));
	}, [planDetails?.document]);

	/* ---------------- HANDLERS ---------------- */

	const handlePdfViewDialogClose = useCallback(() => {
		setOpenPdfViewDialogProps({ openDialog: false });
	}, []);

	const handlePdfViewDialogOpen = useCallback(() => {
		setOpenPdfViewDialogProps({ openDialog: true });
	}, []);

	const handleImageDialogClose = useCallback(() => {
		setImageDialogProps({ openDialog: false, image: undefined });
	}, []);

	const handleViewDocumentClick = (file: MulterGridFSFile) => {
		if (file.mimetype.startsWith("image/")) {
			setImageDialogProps({
				openDialog: true,
				image: generateImageUrl(file)
			});
		}

		if (file.mimetype === "application/pdf") {
			const pdfURL = generateFileURL(file);
			setPdfProps(pdfURL);
			setEventName(file.originalname);
			handlePdfViewDialogOpen();
		}
	};

	const handleDocumentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0];
		if (!file) return;

		setVEBPlanDocument({ document: file });
		event.target.value = "";
	};

	const handleDocumentUpdate = async () => {
		if (!vebPlanDocument?.document) {
			alert("Please select a document first");
			return;
		}

		if (!planDetails?._id) {
			alert("No plan ID detected");
			return;
		}

		const formData = new FormData();

		formData.append(
			"plan",
			JSON.stringify({
				_id: planDetails._id,
				group_number: queryGroupNumber,
				document: null
			})
		);

		formData.append("document", vebPlanDocument.document);

		try {
			await trackPromise(api.plan.updatePlanDocument(formData));
			alert("Document updated successfully");
			setVEBPlanDocument(null);
			window.location.reload();
		} catch (error) {
			console.error(error);
			alert("Failed to update document");
		}
	};

	const toDisplayDate = (value?: string | Date): string => {
		if (!value) return "N/A";
		if (value instanceof Date) return value.toLocaleDateString();
		return value;
	};

	return (
		<div>
			<Suspense fallback={<div />}>
				<LazyCustomImageDialogWithTitle
					dialogProps={imageDialogProps}
					handleImageDialogClose={handleImageDialogClose}
					eventName={eventName}
					eventDate={null}
				/>
			</Suspense>

			<Suspense fallback={<div />}>
				<LazyCustomPdfViewWithTitleDialog
					dialogProps={openPdfViewDialogProps}
					handleClose={handlePdfViewDialogClose}
					pdfFile={pdfProps}
					eventName={eventName}
					eventDate={null}
				/>
			</Suspense>

			{/* ================= STANDARD PLAN ================= */}
			<Paper
				elevation={3}
				variant="outlined"
				sx={{
					border: "1px solid #e0e0e0",
					borderRadius: 2,
					backgroundColor: "#fff",
					mx: 2,
					mt: 2,
					px: 2,
					pt: 3,
					"& .pf-label": { mb: 0.5 },
					"& .field-value": { fontWeight: 600, color: "#333" }
				}}
			>
				<Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
					Standard Plan
				</Typography>

				<Grid container spacing={1}>
					<Grid item xs={12}>
						<PlanInfoRow label="Plan Name">
							<InfoRow value={planDetails?.plan_name} />
						</PlanInfoRow>

						<PlanInfoRow label="Plan Code">
							<InfoRow value={planDetails?.plan_code} />
						</PlanInfoRow>

						<PlanInfoRow label="Group Number">
							<InfoRow value={planDetails?.group_number} />
						</PlanInfoRow>

						<PlanInfoRow label="Eligible Employee Class">
							<InfoRow value={planDetails?.employee_class} />
						</PlanInfoRow>

						<PlanInfoRow label="Current Plan Status">
							<InfoRow
								value={
									planDetails.status.charAt(0).toUpperCase() +
									planDetails.status.slice(1).toLowerCase()
								}
							/>
						</PlanInfoRow>

						<PlanInfoRow label="Automatic Approval">
							<InfoRow value={planDetails?.auto_approve} />
						</PlanInfoRow>

						<PlanInfoRow label="Plan Start Date">
							<InfoRow value={dateTimeZoneConverter(planDetails.start_date).toString()} />
						</PlanInfoRow>

						<PlanInfoRow label="Plan End Date">
							<InfoRow value={toDisplayDate(planDetails?.end_date)} />
						</PlanInfoRow>
						<PlanInfoRow label="Plan order">
							<InfoRow value={planDetails?.plan_order} />
						</PlanInfoRow>
						{/* DOCUMENT SECTION: Only under Standard Plan */}
						<PlanInfoRow label="Plan Documents">
							<div
								style={{
									display: "flex",
									alignItems: "center",
									gap: 8,
									flexWrap: "wrap"
								}}
							>
								{planDetails?.document ? (
									<>
										<Tooltip title="View Document">
											<IconButton
												onClick={() =>
													handleViewDocumentClick(planDetails.document as MulterGridFSFile)
												}
												disabled={!viewableDocument}
											>
												{viewableDocument ? <VisibilityIcon /> : <VisibilityOffIcon />}
											</IconButton>
										</Tooltip>

										<Tooltip title="Download Document">
											<IconButton onClick={() => downloadFileFromMongoDB(planDetails.document)}>
												<GetAppRoundedIcon />
											</IconButton>
										</Tooltip>
									</>
								) : (
									<>
										<span>Not Available</span>
										<IconButton>
											<ErrorIcon />
										</IconButton>
									</>
								)}

								<input
									className={"document-upload"}
									id={"document-upload"}
									name={"Document"}
									type="file"
									onChange={handleDocumentChange}
									style={{ display: "none" }}
									accept="image/*, .pdf"
								/>

								<label htmlFor="document-upload">
									<Button
										className="button"
										component="span"
										variant="contained"
										style={{
											backgroundColor: "#3A4652",
											color: "#ffff",
											width: 210
										}}
									>
										<span className="button-label-with-icon" style={{ color: "#ffff" }}>
											{"Edit Document"}
										</span>
									</Button>
								</label>

								<Button
									variant="contained"
									disabled={!vebPlanDocument?.document}
									onClick={handleDocumentUpdate}
								>
									Update Document
								</Button>
							</div>
						</PlanInfoRow>
						<PlanInfoRow label="Premium depend on smoke">
							<InfoRow value={planDetails?.is_premium_depends_on_smoke} />
						</PlanInfoRow>
						<PlanInfoRow label="beneficiaries_required">
							<InfoRow value={planDetails?.beneficiaries_required} />
						</PlanInfoRow>

						<PlanInfoRow label="Coverage For">
							<CoverageForCard coverageFor={planDetails?.coverage_for} />
						</PlanInfoRow>

						<PlanInfoRow label="Coverage Volume">
							<CoverageForCard coverageFor={planDetails?.coverage_volume} />
						</PlanInfoRow>

						<PlanInfoRow label="Pay Frequency">
							<CoverageForCard coverageFor={planDetails?.pay_frequency} />
						</PlanInfoRow>

						<PlanInfoRow label="Standard Dependencies">
							<StandardPlanDependencyCard dependencies={planDetails?.standard_plan_dependency} />
						</PlanInfoRow>

						<PlanInfoRow label="Rider Dependencies">
							<StandardPlanDependencyCard dependencies={planDetails?.rider_plan_dependency} />
						</PlanInfoRow>

						<PlanInfoRow label="Benefit Amount">
							<BenefitAmountCard benefitAmount={planDetails?.benefit_amount} />
						</PlanInfoRow>

						<PlanInfoRow label="Age Criteria">
							<AgeCriteriaCard ageCriteria={planDetails?.age_criteria} />
						</PlanInfoRow>

						<PlanInfoRow label="Dependent Eligibility">
							<DependentEligibilityCard dependentEligibility={planDetails?.dependent_eligibility} />
						</PlanInfoRow>
					</Grid>
				</Grid>
			</Paper>

			{planDetails?.is_this_plan_has_riders && (
				<Paper
					elevation={3}
					variant="outlined"
					sx={{
						border: "1px solid #e0e0e0",
						borderRadius: 2,
						backgroundColor: "#fff",
						mx: 2,
						mt: 3,
						px: 2,
						pt: 3,
						"& .pf-label": { mb: 0.5 },
						"& .field-value": { fontWeight: 600, color: "#333" }
					}}
				>
					<Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
						Rider Plan
					</Typography>

					<Grid container spacing={1}>
						<Grid item xs={12}>
							<PlanInfoRow label="Rider Plan Name">
								<InfoRow value={planDetails?.rider?.name} />
							</PlanInfoRow>

							<PlanInfoRow label="Coverage For">
								<CoverageForCard coverageFor={planDetails?.rider?.coverage_for} />
							</PlanInfoRow>

							<PlanInfoRow label="Coverage Volume">
								<CoverageForCard coverageFor={planDetails?.rider?.coverage_volume} />
							</PlanInfoRow>

							<PlanInfoRow label="Pay Frequency">
								<CoverageForCard coverageFor={planDetails?.rider?.pay_frequency} />
							</PlanInfoRow>

							<PlanInfoRow label="Benefit Amount">
								<BenefitAmountCard benefitAmount={planDetails?.rider?.benefit_amount} />
							</PlanInfoRow>

							<PlanInfoRow label="Age Criteria">
								<AgeCriteriaCard ageCriteria={planDetails?.rider?.age_criteria} />
							</PlanInfoRow>

							<PlanInfoRow label="Dependent Eligibility">
								<DependentEligibilityCard
									dependentEligibility={planDetails?.rider?.dependent_eligibility}
								/>
							</PlanInfoRow>
						</Grid>
					</Grid>
				</Paper>
			)}
		</div>
	);
};

export default VEBDialogStaticForm;
